import React, { useState, useEffect } from 'react';
import { Lock, Calendar, Settings as SettingsIcon } from 'lucide-react';
import ApiService from '../../services/api';

interface AdminSettingsProps {
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  isAuthenticated: boolean;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ setLoading, setError, isAuthenticated }) => {
  const [profileData, setProfileData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [appointmentSettings, setAppointmentSettings] = useState({
    appointment_capacity_per_slot: '1'
  });
  const [homeSettings, setHomeSettings] = useState<any>(null);

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response);
      } catch (error) {
        console.error('Home settings yüklenirken hata:', error);
      }
    };
    loadHomeSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getSettings();
      if (response.settings) {
        setAppointmentSettings(prev => ({
          ...prev,
          ...response.settings
        }));
      }
    } catch (error: any) {
      console.error('Settings yüklenirken hata:', error);
      // Don't show error for settings load failure
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentSettingsUpdate = async () => {
    try {
      setLoading(true);
      await ApiService.updateSettings(appointmentSettings);
      setError(null);
      // Show success message
      alert('Randevu ayarları başarıyla güncellendi!');
    } catch (error: any) {
      setError('Randevu ayarları güncellenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };



  const handlePasswordChange = async () => {
    if (profileData.newPassword !== profileData.confirmPassword) {
      setError('Yeni şifreler eşleşmiyor');
      return;
    }

    if (profileData.newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      setLoading(true);
      await ApiService.changePassword({
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword,
        confirmPassword: profileData.confirmPassword
      });
      
      setError(null);
      alert('Şifre başarıyla değiştirildi!');
      
      // Form'u temizle
      setProfileData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      setError('Şifre değiştirilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 text-white">
      <h2 className="text-2xl font-bold text-white">Admin Ayarları</h2>



      {/* Şifre Değiştirme */}
              <div className="rounded-lg shadow-sm p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          <div className="flex items-center space-x-3 mb-4">
            <Lock className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-white">Şifre Değiştir</h3>
          </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mevcut Şifre
            </label>
            <input
              type="password"
              value={profileData.currentPassword}
              onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Yeni Şifre
            </label>
            <input
              type="password"
              value={profileData.newPassword}
              onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Yeni Şifre Tekrar
            </label>
            <input
              type="password"
              value={profileData.confirmPassword}
              onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
              style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handlePasswordChange}
            className="flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all"
          >
            <Lock className="w-4 h-4" />
            <span>Şifreyi Değiştir</span>
          </button>
        </div>
      </div>

      {/* Randevu Ayarları */}
              <div className="rounded-lg shadow-sm p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          <div className="flex items-center space-x-3 mb-4">
            <Calendar className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-white">Randevu Ayarları</h3>
          </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Her Saate Maksimum Randevu Sayısı
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max="10"
                value={appointmentSettings.appointment_capacity_per_slot}
                onChange={(e) => setAppointmentSettings(prev => ({ 
                  ...prev, 
                  appointment_capacity_per_slot: e.target.value 
                }))}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
              />
              <span className="text-sm text-gray-600">kişi</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Her 1 saatlik zaman diliminde kaç kişinin randevu alabileceğini belirler
            </p>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleAppointmentSettingsUpdate}
            className="flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all"
          >
            <SettingsIcon className="w-4 h-4" />
            <span>Randevu Ayarlarını Güncelle</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;